import { createRoot } from "react-dom/client";
import { RouterProvider, useRouter } from "./router";
import { SessionProvider, useSession } from "./session";
import { Login } from "./screens/Login";
import { Invite } from "./screens/Invite";
import { SelectOrg } from "./screens/SelectOrg";
import { Signup } from "./screens/Signup";
import { Home } from "./screens/Home";
import { Shell } from "./shell";
import { Patients } from "./screens/Patients";
import { PatientForm } from "./screens/PatientForm";
import { PatientProfile } from "./screens/PatientProfile";
import { Duplicates } from "./screens/Duplicates";
import { Appointments, BookAppointment } from "./screens/Appointments";
import { Doctors, DoctorDetail } from "./screens/Doctors";
import { Messages } from "./screens/Messages";
import { Queue } from "./screens/Queue";
import { Display } from "./screens/Display";
import { Consultation } from "./screens/Consultation";
import { PrintPrescription, PrintCertificate, PrintInvoice, PrintReceipt, PrintLabReport, PrintLabLabels } from "./screens/Print";
import { LabDesk, LabRequestPage } from "./screens/Lab";
import { BillingDesk, BillPage } from "./screens/Billing";
import { WebsiteAdmin } from "./screens/Website";
import { PlatformConsole } from "./screens/PlatformConsole";
import { Profile } from "./screens/Profile";

function Routes() {
  const { path, navigate } = useRouter();
  const { ready, me } = useSession();
  const display = /^\/display\/([A-Za-z0-9_-]{43})$/.exec(path);
  if (display) return <Display token={display[1]!} />;
  const invite = /^\/invite\/([A-Za-z0-9_-]{20,100})$/.exec(path);
  if (invite) return <Invite token={invite[1]!} />;
  if (path === "/signup") return <Signup />;
  if (!ready) return <div className="boot" aria-busy="true" />;
  if (path === "/login") return <Login />;
  if (path === "/select-organisation") return <SelectOrg />;
  if (path === "/platform") { if (!me) { queueMicrotask(() => navigate("/login", true)); return null; } return <PlatformConsole />; }
  const labPrint = /^\/print\/(lab|lab-labels)\/([0-9a-f-]{36})$/.exec(path);
  if (labPrint) {
    if (!me) { queueMicrotask(() => navigate("/login", true)); return null; }
    return labPrint[1] === "lab" ? <PrintLabReport id={labPrint[2]!} /> : <PrintLabLabels id={labPrint[2]!} />;
  }
  const print = /^\/print\/(prescription|certificate|invoice|receipt)\/([0-9a-f-]{36})(?:\/([0-9a-f-]{36}))?$/.exec(path);
  if (print) {
    if (!me) { queueMicrotask(() => navigate("/login", true)); return null; }
    return print[1] === "prescription" ? <PrintPrescription id={print[2]!} /> : print[1] === "certificate" ? <PrintCertificate id={print[2]!} />
      : print[1] === "invoice" ? <PrintInvoice id={print[2]!} /> : <PrintReceipt id={print[2]!} paymentId={print[3]!} />;
  }
  if (path === "/app" || path.startsWith("/app/")) {
    const pid = /^\/app\/patients\/([0-9a-f-]{36})(\/edit)?$/.exec(path);
    const page = path === "/app" ? <Home /> : path === "/app/patients" ? <Patients /> : path === "/app/patients/new" ? <PatientForm />
      : pid ? (pid[2] ? <PatientForm key={path} patientId={pid[1]!} /> : <PatientProfile key={path} id={pid[1]!} />)
      : path === "/app/duplicates" ? <Duplicates /> : path === "/app/appointments" ? <Appointments key={location.search} /> : path === "/app/appointments/new" ? <BookAppointment />
      : path === "/app/doctors" ? <Doctors /> : /^\/app\/doctors\/[0-9a-f-]{36}$/.test(path) ? <DoctorDetail key={path} id={path.split("/")[3]!} />
      : path === "/app/messages" ? <Messages /> : path === "/app/queue" ? <Queue key={location.search} />
      : /^\/app\/consultations\/[0-9a-f-]{36}$/.test(path) ? <Consultation key={path} id={path.split("/")[3]!} />
      : path === "/app/lab" ? <LabDesk key={location.search} /> : /^\/app\/lab\/[0-9a-f-]{36}$/.test(path) ? <LabRequestPage key={path} id={path.split("/")[3]!} />
      : path === "/app/profile" ? <Profile /> : path === "/app/website" ? <WebsiteAdmin /> : path === "/app/billing" ? <BillingDesk key={location.search} /> : /^\/app\/bills\/[0-9a-f-]{36}$/.test(path) ? <BillPage key={path} id={path.split("/")[3]!} /> : <p>Page not found.</p>;
    return <Shell>{page}</Shell>;
  }
  queueMicrotask(() => navigate(me ? (me.tenantId ? "/app" : "/select-organisation") : "/login", true));
  return null;
}

createRoot(document.getElementById("root")!).render(
  <RouterProvider><SessionProvider><Routes /></SessionProvider></RouterProvider>,
);
